/**
 * SEO Metadata Configuration for ByteMonk
 *
 * Centralized metadata for all public routes
 * Domain: https://bytemonk-gcect.web.app
 */

export interface SEOMetadata {
  title: string;
  description: string;
  keywords: string[];
  canonical: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile";
}

const DOMAIN = "https://bytemonk-gcect.web.app";
const DEFAULT_OG_IMAGE = `${DOMAIN}/og-image.png`; // TODO: Create this image

export const seoMetadata: Record<string, SEOMetadata> = {
  home: {
    title: "ByteMonk - Technology Club of GCECT | Karmatek Fest",
    description:
      "ByteMonk is the premier technology club of Government College of Engineering and Ceramic Technology (GCECT), Kolkata. Join us for Karmatek, coding competitions, workshops, and tech events.",
    keywords: [
      "ByteMonk",
      "GCECT",
      "Government College of Engineering and Ceramic Technology",
      "Karmatek",
      "technology club",
      "coding club",
      "Kolkata engineering college",
      "tech fest",
      "programming competitions",
      "hackathon",
    ],
    canonical: `${DOMAIN}/`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
  },

  about: {
    title: "About ByteMonk - GCECT Technology Club",
    description:
      "Learn about ByteMonk, the technology club of GCECT dedicated to fostering innovation, coding excellence, and technical education. Meet our team and discover our mission.",
    keywords: [
      "ByteMonk team",
      "GCECT technical club",
      "about ByteMonk",
      "student society",
      "coding community",
      "technical education",
    ],
    canonical: `${DOMAIN}/about-us`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
  },

  karmatek: {
    title: "Karmatek 2026 - Annual Technical Fest by ByteMonk GCECT",
    description:
      "Karmatek 2026 is the flagship annual technical festival organized by ByteMonk at GCECT. Participate in coding competitions, hackathons, technical workshops, and more.",
    keywords: [
      "Karmatek 2026",
      "GCECT fest",
      "technical festival Kolkata",
      "ByteMonk fest",
      "college tech fest",
      "coding competition",
      "hackathon GCECT",
      "technical events",
    ],
    canonical: `${DOMAIN}/karmatek`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
  },

  karmatekLeaderboard: {
    title: "Leaderboard - Karmatek 2026 | ByteMonk GCECT",
    description:
      "Live leaderboard for Karmatek 2026 events. Track scores and standings for all competitions at the annual technical festival organized by ByteMonk at GCECT.",
    keywords: [
      "Karmatek 2026 leaderboard",
      "Karmatek results",
      "GCECT fest leaderboard",
      "ByteMonk competition scores",
      "technical festival standings",
    ],
    canonical: `${DOMAIN}/karmatek/leaderboard`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
  },

  karmatekSchedule: {
    title: "Schedule - Karmatek 2026 | ByteMonk GCECT",
    description:
      "Full event timetable for Karmatek 2026. Browse the day-by-day schedule for all competitions, workshops, and activities at the annual technical festival by ByteMonk GCECT.",
    keywords: [
      "Karmatek 2026 schedule",
      "Karmatek timetable",
      "GCECT fest schedule",
      "ByteMonk event schedule",
      "technical festival schedule",
    ],
    canonical: `${DOMAIN}/karmatek/schedule`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
  },

  events: {
    title: "Events - ByteMonk Technical Activities & Competitions",
    description:
      "Explore upcoming and past technical events organized by ByteMonk GCECT. From coding contests to workshops, seminars, and hands-on training sessions.",
    keywords: [
      "ByteMonk events",
      "GCECT events",
      "coding competitions",
      "technical workshops",
      "programming contests",
      "tech seminars",
    ],
    canonical: `${DOMAIN}/events`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
  },

  gallery: {
    title: "Gallery - ByteMonk Event Photos & Memories",
    description:
      "Browse through photos and memories from ByteMonk events, Karmatek festivals, workshops, and technical activities at GCECT.",
    keywords: [
      "ByteMonk gallery",
      "GCECT photos",
      "Karmatek photos",
      "event gallery",
      "tech fest pictures",
    ],
    canonical: `${DOMAIN}/gallery`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
  },

  contact: {
    title: "Contact ByteMonk - Get in Touch with GCECT Technology Club",
    description:
      "Contact ByteMonk for collaborations, sponsorships, event inquiries, or to join our technology club at GCECT. Reach out to our team.",
    keywords: [
      "contact ByteMonk",
      "GCECT contact",
      "ByteMonk email",
      "technology club contact",
      "sponsorship inquiry",
    ],
    canonical: `${DOMAIN}/contact`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
  },

  faq: {
    title: "FAQ - Frequently Asked Questions | ByteMonk GCECT",
    description:
      "Find answers to frequently asked questions about ByteMonk, Karmatek fest, membership, events, and technical activities at GCECT.",
    keywords: [
      "ByteMonk FAQ",
      "GCECT FAQ",
      "Karmatek FAQ",
      "how to join ByteMonk",
      "technology club questions",
    ],
    canonical: `${DOMAIN}/faq`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
  },

  cpContest: {
    title: "Competitive Programming Contests - ByteMonk Coding Challenges",
    description:
      "Participate in competitive programming contests hosted by ByteMonk. Test your coding skills, solve algorithmic challenges, and compete on the leaderboard.",
    keywords: [
      "competitive programming",
      "coding contest",
      "ByteMonk CP",
      "algorithm challenges",
      "programming competition",
      "coding leaderboard",
    ],
    canonical: `${DOMAIN}/cp-contest`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
  },
};

/**
 * Generate dynamic metadata for event detail pages
 */
export const generateEventMetadata = (
  eventTitle: string,
  eventDescription: string,
  eventId: string,
): SEOMetadata => {
  return {
    title: `${eventTitle} - Karmatek 2026 | ByteMonk GCECT`,
    description:
      eventDescription ||
      `Participate in ${eventTitle} at Karmatek 2026, organized by ByteMonk, the technology club of GCECT.`,
    keywords: [
      eventTitle,
      "Karmatek event",
      "GCECT event",
      "ByteMonk",
      "technical competition",
      "college fest",
    ],
    canonical: `${DOMAIN}/karmatek/event/${eventId}`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "article",
  };
};

/**
 * Default fallback metadata
 */
export const defaultMetadata: SEOMetadata = {
  title: "ByteMonk - Technology Club of GCECT",
  description:
    "ByteMonk is the premier technology club of Government College of Engineering and Ceramic Technology (GCECT), Kolkata.",
  keywords: ["ByteMonk", "GCECT", "technology club"],
  canonical: DOMAIN,
  ogImage: DEFAULT_OG_IMAGE,
  ogType: "website",
};
